import { Banner } from '@components';
import {
  Box,
  Button,
  Fade,
  FormControl,
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
  Typography,
  formControlLabelClasses,
  radioClasses,
  svgIconClasses,
  typographyClasses,
  useTheme,
} from '@mui/material';
import { type FC, useState } from 'react';

interface RedistributeDialogProps {
  isOpen: boolean;
  originalVolume: number;
  currentDistributed: number;
  unit: string;
  onClose: () => void;
  onRedistribute: (useOriginal: boolean) => void;
}

const DURATION = 300;

export const RedistributeDialog: FC<RedistributeDialogProps> = ({
  isOpen,
  originalVolume,
  currentDistributed,
  unit,
  onClose,
  onRedistribute,
}) => {
  const theme = useTheme();
  const [selectedOption, setSelectedOption] = useState<'original' | 'current'>('original');

  return (
    <Modal
      closeAfterTransition
      open={isOpen}
      onClose={onClose}
      slotProps={{ backdrop: { timeout: DURATION } }}
    >
      <Fade in={isOpen} timeout={DURATION}>
        <Box
          sx={{
            outline: 'none',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 2,
          }}
          onClick={onClose}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              width: 1,
              maxWidth: 524,
              padding: 3,
              borderRadius: 2.5,
              backgroundColor: theme.palette.common.white,
              [`& .${typographyClasses.root}`]: { color: theme.palette.SlabGray[900] },
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                <Typography variant='h5/medium' lineHeight='26px'>
                  Redistribute Forecast
                </Typography>
                <Typography variant='body1' lineHeight='24px'>
                  What would you like to redistribute?
                </Typography>
              </Box>

              <Banner severity='warning'>
                This will override your manual edits to individual months.
              </Banner>

              <FormControl component='fieldset'>
                <RadioGroup
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value as 'original' | 'current')}
                  sx={{
                    gap: 1,
                    [`& .${formControlLabelClasses.root}`]: {
                      alignItems: 'flex-start',
                      gap: 1,
                      m: 0,
                    },
                    [`& .${radioClasses.root}`]: { p: 0, mt: 0.25 },
                    [`& .${radioClasses.root} .${svgIconClasses.root}`]: { fontSize: 20 },
                  }}
                >
                  <FormControlLabel
                    value='original'
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant='h6/medium' lineHeight='24px'>
                          Original volume: {Math.round(originalVolume)} {unit}
                        </Typography>
                        <Typography variant='body1' lineHeight='24px'>
                          Reset to the volume from your quote
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value='current'
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant='h6/medium' lineHeight='24px'>
                          Current total distributed: {Math.round(currentDistributed)} {unit}
                        </Typography>
                        <Typography variant='body1' lineHeight='24px'>
                          Redistribute the adjusted total you&apos;ve entered
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Button variant='slab-secondary' fullWidth onClick={onClose} sx={{ flex: 1 }}>
                CANCEL
              </Button>
              <Button
                variant='slab-primary'
                fullWidth
                onClick={() => {
                  onRedistribute(selectedOption === 'original');
                  onClose();
                }}
                sx={{ flex: 1 }}
              >
                REDISTRIBUTE
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};
